import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  IconButton,
} from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { Component } from "../../../../types/component";
import { SubAssembly } from "../../../../types/subAssembly";
import { getComponentTypeName } from "../../../../utils/componentTypeUtils";
import { isLandingGearComponent } from "../../../../utils/landingGearAttachUtils";
import { AttachedLandingGearSubAssemblies } from "./AttachedLandingGearSubAssemblies";

const GROUP_ORDER = ["Engine", "Landing Gear", "APU"] as const;

type Props = {
  components: Component[];
  subAssemblies: SubAssembly[];
  onReplace: (component: Component) => void;
  onDetach: (componentId: string,airframeId?: string,slotId?: string) => void;
  canUpdate?: boolean;
  canDetach?: boolean;
};

export function AttachedComponents({
  components,
  subAssemblies,
  onReplace,
  onDetach,
  canUpdate = true,
  canDetach = true,
}: Readonly<Props>) {
  if (!components.length) {
    return <Typography>No assemblies attached</Typography>;
  }

  const groupedComponents = GROUP_ORDER.map((groupName) => ({
    name: groupName,
    components: components
      .filter((component) => getAssemblyGroup(component) === groupName)
      .sort(compareComponents),
  })).filter((group) => group.components.length > 0);

  return (
    <Box>
      {groupedComponents.map((group) => (
        <Box key={group.name} sx={{ mb: 2.5 }}>
          <Typography
            component="h4"
            variant="subtitle2"
            sx={{
              color: "#aaa",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              mb: 0.75,
            }}
          >
            {group.name}
          </Typography>

          <List dense disablePadding>
            {group.components.map((component) => (
              <AttachedComponentRow
                key={component.id}
                component={component}
                subAssemblies={subAssemblies}
                onReplace={onReplace}
                onDetach={onDetach}
                canUpdate={canUpdate}
                canDetach={canDetach}
              />
            ))}
          </List>
        </Box>
      ))}
    </Box>
  );
}

const getCustomField = (metadata: Record<string, any> | undefined, ...keys: string[]): any => {
  if (!metadata) return undefined;
  return keys.find(key => metadata[key] !== undefined && metadata[key] !== null) 
    ? metadata[keys.find(key => metadata[key] !== undefined && metadata[key] !== null)!]
    : undefined;
};

function AttachedComponentRow({
  component,
  subAssemblies,
  onReplace,
  onDetach,
  canUpdate,
  canDetach,
}: Readonly<{
  component: Component;
  subAssemblies: SubAssembly[];
  onReplace: (component: Component) => void;
  onDetach: (componentId: string, airframeId?: string, slotId?: string) => void;
  canUpdate: boolean;
  canDetach: boolean;
}>) {

  const currentThrust = getCustomField(
      component.customMetadata,
      "engineCurrentThrust",
      "currentThrust"
    );

  return (
    <ListItem
      sx={{
        px: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
        }}
      >
        <ListItemText
          primary={
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box>
                <Typography sx={{ color: "#fff", fontWeight: 500 }}>
                    {component.type === "Engine" && currentThrust ? component.model +  " - " + currentThrust + " - " + component.serialNumber : component.model +  " - " + component.serialNumber}
                </Typography>
                <Typography sx={{ color: "#aaa", fontSize: "0.85rem" }}>
                  {component.attachmentLocation && `Position: ${component.attachmentLocation}`}
                </Typography>
              </Box>

              <Chip
                size="small"
                label={component.type}
                sx={{
                  bgcolor: "hsl(19 101% 51% / 0.2)",
                  color: "hsl(19 101% 51% / 1)",
                }}
              />
            </Box>
          }
        />

        <Box>
          {canUpdate && (
            <IconButton
              size="small"
              aria-label="Replace assembly"
              title="Replace assembly"
              sx={{ color: "hsl(19 101% 51% / 1)", mr: 1 }}
              onClick={() => onReplace(component)}
            >
              <SwapHorizIcon />
            </IconButton>
          )}

          {canDetach && (
            <IconButton
              size="small"
              aria-label="Detach assembly"
              title="Detach assembly"
              sx={{ color: "hsl(19 101% 51% / 1)" }}
              onClick={() => onDetach(component.id, component.airframeId, component.slotId)}
            >
              <LinkOffIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      {isLandingGearComponent(component) && (
        <AttachedLandingGearSubAssemblies componentId={component.id} subAssemblies={subAssemblies} />
      )}
    </ListItem>
  );
}

function compareComponents(left: Component, right: Component) {
  const leftGroup = getAssemblyGroup(left);
  const rightGroup = getAssemblyGroup(right);

  if (leftGroup !== rightGroup) {
    return GROUP_ORDER.indexOf(leftGroup) - GROUP_ORDER.indexOf(rightGroup);
  }

  const bySlotNumber = compareSlotNumbers(left.slotNumber, right.slotNumber);
  if (bySlotNumber !== 0) {
    return bySlotNumber;
  }

  const byPosition = compareAttachmentLocations(left.attachmentLocation, right.attachmentLocation);
  if (byPosition !== 0) {
    return byPosition;
  }

  const byModel = (left.model ?? "").localeCompare(right.model ?? "", undefined, { numeric: true, sensitivity: "base" });
  if (byModel !== 0) {
    return byModel;
  }

  return (left.serialNumber ?? "").localeCompare(right.serialNumber ?? "", undefined, { numeric: true, sensitivity: "base" });
}

function compareAttachmentLocations(
  leftLocation: string | undefined,
  rightLocation: string | undefined,
) {
  const leftPosition = normalizeAttachmentLocation(leftLocation);
  const rightPosition = normalizeAttachmentLocation(rightLocation);

  if (leftPosition.numericSuffix !== rightPosition.numericSuffix) {
    return leftPosition.numericSuffix - rightPosition.numericSuffix;
  }

  return leftPosition.raw.localeCompare(rightPosition.raw, undefined, { numeric: true, sensitivity: "base" });
}

function compareSlotNumbers(leftSlotNumber: number | undefined, rightSlotNumber: number | undefined) {
  if (leftSlotNumber === rightSlotNumber) {
    return 0;
  }

  if (leftSlotNumber === undefined) {
    return 1;
  }

  if (rightSlotNumber === undefined) {
    return -1;
  }

  return leftSlotNumber - rightSlotNumber;
}

function normalizeAttachmentLocation(location: string | undefined) {
  const raw = (location ?? "").trim();
  const normalized = raw.toUpperCase().replace(/[^A-Z0-9]/g, "");
  const numericMatch = normalized.match(/(\d+)$/);

  return {
    raw,
    numericSuffix: numericMatch ? Number.parseInt(numericMatch[1], 10) : 0,
  };
}

function getAssemblyGroup(component: Component): (typeof GROUP_ORDER)[number] {
  const normalizedType = getComponentTypeName(component.type ?? component.componentType);

  if (normalizedType === "Engine") {
    return "Engine";
  }

  if (normalizedType === "Landing Gear") {
    return "Landing Gear";
  }

  return "APU";
}
